import { RARITIES, type ChestSource, type Rarity } from '@/game/chests';
import type { ChestSummary, ChestView } from '@/net/api';

import { chestStore, openChest } from './chests-state';
import { createStore } from './store';

/**
 * Blockari's `state/arrival.ts`, ported: the same rules, a plain store in
 * place of zustand.
 *
 * The chest ARRIVAL's state: which chests have had their arrival (seen,
 * once per chest id, kept in localStorage so a reload never replays one;
 * an in-memory set when storage is unavailable), the batch on screen, and
 * the top bar pill's count while chests are in flight to it.
 *
 * Where arrivals start (`showArrival`):
 *   - the results screen: after the XP beat settles, or straight after the
 *     rank-up's CONTINUE (screens/ResultsXp.tsx `arrivalAfterResults`);
 *   - home: a login-streak chest, or any chest awarded while away (Home.tsx).
 *
 * The pill counts every unopened chest EXCEPT the ones still arriving
 * (`hidden`): LATER's flight lands each in the pill, and only then does it
 * count (`stow`) — the count ticks up as the chest arrives. Dev replays
 * (`?arrival=`) have no server list behind them; they count in `devCount`.
 */

const SEEN_KEY = 'w21.chest.arrived';
/** Enough ids that no real player's backlog is forgotten; the oldest drop off first. */
const SEEN_CAP = 400;

const memory = new Set<string>();

function readSeen(): Set<string> {
  const out = new Set(memory);
  try {
    const raw = globalThis.localStorage?.getItem(SEEN_KEY);
    if (raw) for (const id of JSON.parse(raw) as unknown[]) if (typeof id === 'string') out.add(id);
  } catch {
    // Storage blocked or corrupt: the in-memory set still holds this session's.
  }
  return out;
}

/** Whether this chest's arrival has played (on this device). */
export function arrivalSeen(id: string): boolean {
  return readSeen().has(id);
}

/** Record arrivals as played. Never throws. */
export function markArrivalSeen(ids: readonly string[]): void {
  if (ids.length === 0) return;
  for (const id of ids) memory.add(id);
  try {
    const store = globalThis.localStorage;
    if (!store) return;
    const all = [...readSeen()];
    store.setItem(SEEN_KEY, JSON.stringify(all.slice(-SEEN_CAP)));
  } catch {
    // The memory set has them.
  }
}

/** These chests are owed their arrival again (a guest's, unlocked by registering). */
export function forgetArrivalSeen(ids: readonly string[]): void {
  if (ids.length === 0) return;
  const drop = new Set(ids);
  for (const id of ids) memory.delete(id);
  try {
    const store = globalThis.localStorage;
    if (!store) return;
    const rest = [...readSeen()].filter((id) => !drop.has(id));
    store.setItem(SEEN_KEY, JSON.stringify(rest.slice(-SEEN_CAP)));
  } catch {
    // The memory set no longer has them.
  }
}

/** Test hook: forget every arrival (memory and storage). */
export function resetArrivalSeen(): void {
  memory.clear();
  try {
    globalThis.localStorage?.removeItem(SEEN_KEY);
  } catch {
    // Nothing to forget.
  }
}

/** One chest in an arrival: what earned it, and its stored roll once fetched. */
export interface ArrivalChest extends ChestSummary {
  /** The roll to animate (dev replays carry theirs; real ones are fetched as the arrival starts). */
  roll?: ChestView | undefined;
}

export interface ArrivalBatch {
  /** A new batch remounts the stage. */
  key: number;
  chests: ArrivalChest[];
  /** A dev replay: nothing touches the server. */
  dev: boolean;
}

interface ArrivalState {
  batch: ArrivalBatch | null;
  /** Arriving chests the pill does not count yet. */
  hidden: string[];
  /** Dev chests stowed in the pill. */
  devCount: number;
  /** LATER is flying chests to the pill: it shows even at zero. */
  stowing: boolean;
  /** Bumped on every chest reaching the pill (its burst). */
  burst: number;
  /** The arrival (or the reveal it led into) covers the page: the shell's backdrop parks its loop. */
  covered: boolean;
  /** The batch's stage has drawn its first frame (the rank-up waits for it before it fades). */
  up: boolean;
}

let seq = 0;

export const arrivalStore = createStore<ArrivalState>({
  batch: null,
  hidden: [],
  devCount: 0,
  stowing: false,
  burst: 0,
  covered: false,
  up: false,
});

/** The stage is up and its veil covers the page (a rank-up held over CONTINUE may fade now). */
export function markArrivalUp(): void {
  if (!arrivalStore.getState().up) arrivalStore.setState({ up: true });
}

/** The stage covers the page (or no longer does): the backdrop under it need not animate. */
export function setArrivalCovered(covered: boolean): void {
  if (arrivalStore.getState().covered !== covered) arrivalStore.setState({ covered });
  // The page under a full veil is hidden outright: its text must never read through the ground.
  if (typeof document !== 'undefined') document.body.classList.toggle('w21-arrival-covered', covered);
}

let gate: () => boolean = () => true;

/**
 * The UI's gate (ui/index.ts `setArrivalGate`): an arrival may start on any screen but the game's own.
 * The results screen is 'over', so the results hand-off is allowed there; a results-screen timer that fires
 * after Play again (screen 'game') is not.
 */
export function arrivalAllowedOn(screen: string): boolean {
  return screen !== 'game';
}

/** Whether an arrival may start right now (the UI says: not while a round is on the table). */
export function setArrivalGate(allowed: () => boolean): void {
  gate = allowed;
}

/**
 * Play the arrival for these chests: the unopened ones whose arrival has
 * not played yet, oldest first. They are marked seen as it starts (a reload
 * mid-arrival never replays it; the pill still lists them). A batch already
 * on screen takes nothing new: the newcomers wait for the next trigger.
 * Returns whether an arrival started.
 */
export function showArrival(
  chests: readonly ArrivalChest[],
  opts: { dev?: boolean } = {},
): boolean {
  const s = arrivalStore.getState();
  if (s.batch) return false;
  // Never over a round: the arrival's veil hides the table and its page, so one that lands on the game
  // screen (a results-screen timer firing after the player has pressed Play again) leaves a ranked
  // round running, stake taken, behind a screen the player cannot see. The chests wait in the pill.
  if (!opts.dev && !gate()) return false;
  const seen = readSeen();
  const fresh: ArrivalChest[] = [];
  const ids = new Set<string>();
  for (const c of chests) {
    if (c.opened || ids.has(c.id) || (!opts.dev && seen.has(c.id))) continue;
    ids.add(c.id);
    fresh.push(c);
  }
  if (fresh.length === 0) return false;
  fresh.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  if (!opts.dev) markArrivalSeen(fresh.map((c) => c.id));
  arrivalStore.setState({
    batch: { key: ++seq, chests: fresh, dev: opts.dev === true },
    hidden: fresh.map((c) => c.id),
    stowing: false,
    up: false,
  });
  return true;
}

/** LATER pressed: the pill comes up (even at zero) to catch the flights. */
export function beginStow(): void {
  arrivalStore.setState({ stowing: true });
}

/** A chest reached the pill: it counts from now (and the pill bursts). */
export function stowChest(id: string): void {
  const s = arrivalStore.getState();
  const batch = s.batch;
  const chest = batch?.chests.find((c) => c.id === id);
  if (!chest || !s.hidden.includes(id)) return;
  if (batch?.dev) {
    arrivalStore.setState({
      hidden: s.hidden.filter((h) => h !== id),
      devCount: s.devCount + 1,
      burst: s.burst + 1,
    });
    return;
  }
  // The server's list may not have caught up with the award yet: the pill must count it now.
  const chests = chestStore.getState();
  if (!chests.unopened.some((c) => c.id === id)) {
    const summary: ChestSummary = {
      id: chest.id,
      source: chest.source,
      startRarity: chest.startRarity,
      createdAt: chest.createdAt,
      opened: false,
    };
    chestStore.setState({ unopened: [...chests.unopened, summary] });
  }
  arrivalStore.setState({ hidden: s.hidden.filter((h) => h !== id), burst: s.burst + 1 });
}

/** A chest from the batch is being opened (TAP TO OPEN, or the next in line): the pill never counts it. */
export function arrivalOpened(id: string): void {
  const s = arrivalStore.getState();
  chestStore.setState({ unopened: chestStore.getState().unopened.filter((c) => c.id !== id) });
  arrivalStore.setState({ hidden: s.hidden.filter((h) => h !== id) });
}

/** The arrival (and any reveal it led into) is over. */
export function endArrival(): void {
  const s = arrivalStore.getState();
  // Anything still hidden was neither opened nor stowed (the layer closed early): the pill counts it.
  arrivalStore.setState({
    batch: null,
    hidden: [],
    stowing: false,
    devCount: s.devCount,
    burst: 0,
    covered: false,
  });
}

/** The pill's count: every unopened chest not still arriving, plus stowed dev chests. */
export function pillCount(
  unopened: readonly ChestSummary[],
  s: Pick<ArrivalState, 'hidden' | 'devCount'>,
): number {
  return unopened.filter((c) => !s.hidden.includes(c.id)).length + s.devCount;
}

/** The pill's count now (from both stores). */
export function currentPillCount(): number {
  return pillCount(chestStore.getState().unopened, arrivalStore.getState());
}

/** After a results screen's XP beat settles, its chests arrive this much later (ms on the beat's clock). */
export const RESULTS_ARRIVAL_HOLD_MS = 700;

/**
 * Whether the results screen's chests arrive now: with a rank-up, straight
 * on its CONTINUE — the arrival's veil rises under the rank-up's as that one
 * fades (it sits above), so the results never show between the two; this
 * replaced the jump into the reveal. Otherwise once the beat has settled and
 * held.
 */
export function resultsArrivalDue(p: {
  kind: 'rank' | 'level' | null;
  /** The beat's clock (ms). */
  e: number;
  endMs: number;
  /** CONTINUE pressed. */
  dismissed: boolean;
}): boolean {
  if (p.kind === 'rank') return p.dismissed;
  return p.e >= p.endMs + RESULTS_ARRIVAL_HOLD_MS;
}

/**
 * The results screen's hand-off: the chests this game earned arrive (after
 * the XP beat settles, or straight after the rank-up's CONTINUE — in place
 * of the old jump into the reveal). `dev` (a `?arrival=` replay) stands in
 * for them. When every one has already had its arrival, the rank-up's chest
 * opens as it used to. Returns whether an arrival started.
 */
export function chestsArriveAfterResults(
  chests: readonly ChestSummary[] | undefined,
  dev: ArrivalChest[] | null,
): boolean {
  if (dev) return showArrival(dev, { dev: true });
  const list = (chests ?? []).filter((c) => !c.opened);
  if (list.length === 0) return false;
  if (showArrival(list)) return true;
  // One is on screen already (it takes nothing new): leave these to the pill.
  if (arrivalStore.getState().batch) return false;
  const rank = list.find((c) => c.source === 'rank_up');
  if (rank) openChest(rank.id);
  return false;
}

/* --------------------------------------------------------------------------
   The dev replay: `?arrival=<source>[,<source>…]&rarity=<r>[,<r>…]&n=<count>[&steps=rare,epic]`
   -------------------------------------------------------------------------- */

const SOURCES: readonly ChestSource[] = [
  'rank_up',
  'first_win',
  'wins_10',
  'wins_50',
  'wins_100',
  'win_streak_3',
  'login_streak_7',
  'weekly_top',
  'welcome',
];

const ALIAS: Record<string, ChestSource> = {
  rank: 'rank_up',
  rankup: 'rank_up',
  first: 'first_win',
  win10: 'wins_10',
  streak: 'win_streak_3',
  login: 'login_streak_7',
  daily: 'login_streak_7',
  weekly: 'weekly_top',
  gift: 'welcome',
};

/** The dev arrival's batch from a query string, or null when it asks for none. */
export function devArrivalChests(
  search: string,
  roll: (id: string, source: ChestSource, start: Rarity, steps: Rarity[]) => ChestView,
): ArrivalChest[] | null {
  const q = new URLSearchParams(search);
  const raw = q.get('arrival');
  if (raw === null) return null;
  const sources = raw
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .map(
      (s) => ALIAS[s] ?? ((SOURCES as readonly string[]).includes(s) ? (s as ChestSource) : null),
    )
    .filter((s): s is ChestSource => s !== null);
  const rarities = (q.get('rarity') ?? 'common')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((s): s is Rarity => (RARITIES as readonly string[]).includes(s));
  const steps = (q.get('steps') ?? '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((s): s is Rarity => (RARITIES as readonly string[]).includes(s));
  const n = Math.max(1, Math.min(9, Number(q.get('n') ?? sources.length) || 1));
  // A weekly-top chest's ref (`&ref=xp:2026-W40:1`), for its "1ST PLACE · WEEKLY XP" label.
  const refs = (q.get('ref') ?? '').split(',').filter(Boolean);
  return Array.from({ length: n }, (_, i) => {
    const source = sources[i] ?? sources[sources.length - 1] ?? 'rank_up';
    const start = rarities[i] ?? rarities[rarities.length - 1] ?? 'common';
    // Steps climb from the start (the first chest's, or a default one rung up for the rest).
    const climb = steps.filter((r) => RARITIES.indexOf(r) > RARITIES.indexOf(start));
    const id = `dev-arrival-${i}`;
    return {
      id,
      source,
      ...(refs.length > 0 ? { sourceRef: refs[i] ?? refs[refs.length - 1] } : {}),
      startRarity: start,
      createdAt: new Date(i * 1000).toISOString(),
      opened: false,
      roll: roll(id, source, start, climb),
    };
  });
}
